import { Editor, Node, mergeAttributes } from '@tiptap/core';
import { EditorState, Plugin, PluginKey, Selection, Transaction } from '@tiptap/pm/state';
import { Node as PMNode } from '@tiptap/pm/model';

/**
 * Unified inserted-item token.
 *
 * ONE atom node backs both insertion paths — drag/drop+paste upload and the "/"
 * search picker — so a file added either way renders identically. It replaces the
 * two older nodes (`imageFileCard` for uploads, `mentionCard` for search), whose
 * saved markup is still accepted on parse (see `parseHTML` legacy rules) so notes
 * written before this change keep rendering.
 *
 * Rendered as a compact inline link: a small type icon + the item name underlined
 * in blue, sitting on the text baseline and wrapping like a word. The full path /
 * size lives in the hover tooltip.
 *
 * CLICK HANDLING — deliberately NOT bound in the node view. Listeners attached to
 * node-view DOM capture the attrs present when the view was built, and the view is
 * rebuilt on every surrounding-text edit; that is what made tokens go dead while
 * proofreading. Instead a single ProseMirror `handleClickOn` plugin reads the LIVE
 * node out of the document at click time, so a token stays clickable through any
 * number of re-renders and never routes on stale attrs.
 */

export type ReferenceTokenType = 'file' | 'task' | 'client' | 'folder';
export type ReferenceFileKind = 'image' | 'doc' | 'xls' | 'ppt' | 'pdf' | 'generic';
export type ReferenceTokenStatus = 'ready' | 'uploading' | 'error' | 'deleted';

export interface ReferenceTokenAttrs {
    /** Drives the icon and the click route. */
    type: ReferenceTokenType;
    /** Only meaningful when `type === 'file'` — selects the file icon. */
    fileKind: ReferenceFileKind | null;
    /** Graph item id for files/folders; the record id (as a string) for task/client. */
    itemId: string;
    driveId: string;
    name: string;
    /** SharePoint / OneDrive URL for files, record URL for task/client/folder. */
    url: string;
    status: ReferenceTokenStatus;

    // --- Routing + tooltip detail (kept so click routing and hover survive a save/reload) ---
    siteId: string;
    /** Parent folder's Graph item id. */
    folderId: string;
    /** Path shown in the tooltip, e.g. "Trovve / User Stories". */
    breadcrumb: string;
    /** Overrides `breadcrumb` in the tooltip when set (clients show "Customer › Active"). */
    tooltipBreadcrumb: string;
    fileSize: number;
    extension: string;
    entityId: number;
    entityType: number;
    taskId: number;
    contactId: number;
    companyId: number;
    /** Stable per-insert key so the DOM is identifiable across re-renders. */
    refKey: string;
}

export interface ReferenceTokenOptions {
    HTMLAttributes: Record<string, any>;
    /** Image file token clicked → open the in-app lightbox. */
    onImageClick: (attrs: ReferenceTokenAttrs) => void;
    /** Non-image file token clicked → open in Office / SharePoint. */
    onFileClick: (attrs: ReferenceTokenAttrs) => void;
    onFolderClick: (attrs: ReferenceTokenAttrs) => void;
    onTaskClick: (attrs: ReferenceTokenAttrs) => void;
    onClientClick: (attrs: ReferenceTokenAttrs) => void;
}

declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        referenceToken: {
            insertReferenceToken: (attrs: Partial<ReferenceTokenAttrs>) => ReturnType;
        };
    }
}

export const ReferenceTokenPluginKey = new PluginKey('vfReferenceTokenClick');

const REFERENCE_TOKEN_NAME: string = 'referenceToken';

/**
 * Builds a transaction stripping every mark off token nodes, or null if there is
 * nothing to strip.
 *
 * A token must never carry a mark. Note that `NodeSpec.marks` can't express this
 * — in ProseMirror that field constrains the marks allowed INSIDE a node's
 * content, not the marks applied TO it (those are governed by the parent). So it
 * is enforced here instead. Two things this prevents:
 *   - Bolding/underlining a selection that spans a token mutating the token.
 *   - Style-matching mark rules latching onto token markup on parse. An early
 *     build emitted `text-decoration: underline` inline, which Underline's second
 *     parse rule matches, so every reload wrapped each token in a fresh <u> —
 *     visible as a stuck-active "U" in the toolbar. That cause is gone, and this
 *     also cleans the <u> out of notes already saved with it.
 */
function buildMarkStripTr(state: EditorState): Transaction | null {
    let tr: Transaction | null = null;

    state.doc.descendants((node, pos) => {
        if (node.type.name !== REFERENCE_TOKEN_NAME || node.marks.length === 0) {
            return true;
        }
        tr = tr || state.tr;
        // Markup-only change, so earlier positions stay valid for later hits.
        tr.setNodeMarkup(pos, undefined, node.attrs, []);
        return false;
    });
    if (tr) {
        // Not a user edit — keep it out of the undo stack.
        tr.setMeta('addToHistory', false);
    }
    return tr;
}

/**
 * Strips token marks once for the editor's INITIAL document. `appendTransaction`
 * covers every later change, but content passed to `new Editor({ content })`
 * never produces a transaction, so it would otherwise stay dirty until the first
 * edit. Called from the host's onCreate.
 */
export function stripReferenceTokenMarks(editor: Editor): void {
    const tr: Transaction | null = buildMarkStripTr(editor.state);

    if (tr) {
        editor.view.dispatch(tr);
    }
}

const IMAGE_EXTS: string[] = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp', 'heic', 'heif', 'tif', 'tiff'];
// STRICTLY the formats that own the brand icon. `.md`, `.txt`, `.rtf`, `.odt`,
// `.csv` etc. are NOT Word/Excel documents and must fall through to 'generic' —
// showing a Word icon on a Markdown file is simply wrong. Mirrors the extension
// list in `FileIconComponent.getExt()`, which is where these icons come from.
const DOC_EXTS: string[] = ['doc', 'docx'];
const XLS_EXTS: string[] = ['xls', 'xlsx'];
const PPT_EXTS: string[] = ['ppt', 'pptx'];

/** Maps a bare file extension (no dot, any case) to the icon bucket. */
export function fileKindFromExtension(extension: string): ReferenceFileKind {
    const ext: string = (extension || '').toLowerCase().replace(/^\./, '');

    if (IMAGE_EXTS.indexOf(ext) >= 0) {
        return 'image';
    }
    if (DOC_EXTS.indexOf(ext) >= 0) {
        return 'doc';
    }
    if (XLS_EXTS.indexOf(ext) >= 0) {
        return 'xls';
    }
    if (PPT_EXTS.indexOf(ext) >= 0) {
        return 'ppt';
    }
    if (ext === 'pdf') {
        return 'pdf';
    }
    return 'generic';
}

/** Extension (no dot, lowercased) from a file name; '' when there isn't one. */
export function extensionFromFileName(fileName: string): string {
    const name: string = fileName || '';
    const dot: number = name.lastIndexOf('.');

    if (dot < 0 || dot === name.length - 1) {
        return '';
    }
    return name.substring(dot + 1).toLowerCase();
}

export function escapeHtml(value: string): string {
    return (value || '').replace(/[&<>"']/g, (c) => {
        switch (c) {
            case '&': return '&amp;';
            case '<': return '&lt;';
            case '>': return '&gt;';
            case '"': return '&quot;';
            case '\'': return '&#39;';
            default: return c;
        }
    });
}

export function formatFileSize(bytes: number): string {
    let size = 0;

    if (!bytes || bytes <= 0) {
        return '';
    }
    if (bytes < 1024) {
        return bytes + ' B';
    }
    if (bytes < 1024 * 1024) {
        size = bytes / 1024;
        return Math.round(size) + ' KB';
    }
    size = bytes / (1024 * 1024);
    return size.toFixed(1) + ' MB';
}

// --- Icons -------------------------------------------------------------------
// Declared once and emitted two ways: as a markup string for the node view
// (innerHTML) and as a ProseMirror DOMOutputSpec array for renderHTML. Emitting
// the real icon — rather than an empty placeholder the node view fills in — keeps
// it present in SAVED html, so read-only surfaces and notes copied into
// Outlook/Word still show it.
//
// Office types use the same hosted brand SVGs the Files tab uses via
// `FileIconComponent.getExt()`, so a docx looks the same in the notes as it does
// in the file list. Everything else is an inline stroke icon.

const FILE_ICON_CDN: string = 'https://cdn.trovve.com/vfapp/images/file-icons/';

export interface IconDef {
    /** Hosted brand icon (Office types). */
    src?: string;
    /** Emoji glyph — beats a hand-drawn SVG for the types the platform already draws well. */
    glyph?: string;
    /** Inline stroke icon. */
    color?: string;
    paths?: string[];
    /** Paint the paths as a SOLID fill in `color` instead of stroking them. */
    filled?: boolean;
}

export const ICON_DEFS: Record<string, IconDef> = {
    doc: { src: FILE_ICON_CDN + 'word.svg' },
    xls: { src: FILE_ICON_CDN + 'excel.svg' },
    ppt: { src: FILE_ICON_CDN + 'powerpoint.svg' },
    image: {
        color: '#6B7280',
        paths: ['M3 5.5A1.5 1.5 0 0 1 4.5 4h15A1.5 1.5 0 0 1 21 5.5v13a1.5 1.5 0 0 1-1.5 1.5h-15A1.5 1.5 0 0 1 3 18.5z',
            'M8.5 10.5a1.25 1.25 0 1 0 0-2.5 1.25 1.25 0 0 0 0 2.5', 'm21 16-5-5L6 20']
    },
    pdf: {
        color: '#D93025',
        paths: ['M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z', 'M14 2v6h6',
            'M8 17v-4.5h2a1.5 1.5 0 0 1 0 3H8', 'M13.5 17v-4.5h1.5a2.25 2.25 0 0 1 0 4.5z']
    },
    generic: {
        color: '#6B7280',
        paths: ['M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z', 'M14 2v6h6']
    },
    // The platform emoji, same as the email reply composer inlines. A folder is read by silhouette and
    // the OS draws a better two-tone one than a flat single-colour path ever did — outline looked
    // hollow, solid amber looked like a blob.
    folder: { glyph: '\u{1F4C1}' },
    task: {
        color: '#16A34A',
        paths: ['M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18', 'm8.75 12 2.25 2.25 4.25-4.5']
    },
    client: {
        color: '#6B7280',
        paths: ['M17 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2', 'M9.5 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8',
            'M22 21v-2a4 4 0 0 0-3-3.87', 'M16 3.13a4 4 0 0 1 0 7.75']
    }
};

function iconKeyFor(type: ReferenceTokenType, fileKind: ReferenceFileKind | null): string {
    switch (type) {
        case 'task': return 'task';
        case 'client': return 'client';
        case 'folder': return 'folder';
        case 'file': return fileKind && ICON_DEFS[fileKind] ? fileKind : 'generic';
        default: return 'generic';
    }
}

/**
 * A `filled` def paints its paths solid and drops the stroke; everything else is a stroke outline on a
 * transparent body. Shared by both emitters below AND by `vf-attachment-icon`, so an icon can't come out
 * filled in one surface and hollow in another.
 */
export function iconFill(def: IconDef): string {
    return def.filled ? def.color || '#6B7280' : 'none';
}

export function iconStroke(def: IconDef): string {
    return def.filled ? 'none' : def.color || '#6B7280';
}

/**
 * A glyph icon sits in the same 14px box the `<img>`/`<svg>` icons do. `line-height` is pinned so the
 * emoji doesn't push the line taller than the surrounding text, and the colour is reset because the
 * token itself is blue — an emoji inherits nothing, but a monochrome fallback glyph would.
 */
const GLYPH_INLINE_STYLE: string = 'font-size:14px;line-height:14px;color:initial;';

/** Markup string — used by the node view (innerHTML). */
function iconMarkup(type: ReferenceTokenType, fileKind: ReferenceFileKind | null): string {
    const def: IconDef = ICON_DEFS[iconKeyFor(type, fileKind)];
    let out = '';

    if (def.src) {
        return '<img src="' + def.src + '" width="14" height="14" alt="" aria-hidden="true" />';
    }
    if (def.glyph) {
        return '<span style="' + GLYPH_INLINE_STYLE + '" aria-hidden="true">' + def.glyph + '</span>';
    }
    out = '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" ' +
        'fill="' + iconFill(def) + '" stroke="' + iconStroke(def) + '" stroke-width="1.75" ' +
        'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">';
    (def.paths || []).forEach((d) => {
        out += '<path d="' + d + '"/>';
    });
    out += '</svg>';
    return out;
}

/** DOMOutputSpec array — used by renderHTML so the icon survives serialization. */
function iconSpec(type: ReferenceTokenType, fileKind: ReferenceFileKind | null): any[] {
    const def: IconDef = ICON_DEFS[iconKeyFor(type, fileKind)];
    let spec: any[] = [];

    if (def.src) {
        return ['img', { src: def.src, width: '14', height: '14', alt: '', 'aria-hidden': 'true' }];
    }
    if (def.glyph) {
        return ['span', { style: GLYPH_INLINE_STYLE, 'aria-hidden': 'true' }, def.glyph];
    }
    // `xmlns` makes ProseMirror's DOMSerializer use createElementNS, without which
    // the <svg>/<path> elements are built in the HTML namespace and never paint.
    spec = ['svg', {
        xmlns: 'http://www.w3.org/2000/svg',
        width: '14',
        height: '14',
        viewBox: '0 0 24 24',
        fill: iconFill(def),
        stroke: iconStroke(def),
        'stroke-width': '1.75',
        'stroke-linecap': 'round',
        'stroke-linejoin': 'round',
        'aria-hidden': 'true'
    }];
    (def.paths || []).forEach((d) => {
        spec.push(['path', { d }]);
    });
    return spec;
}

// Critical look is ALSO applied inline, not just via the component stylesheet.
// The stylesheet only reaches `.ProseMirror`, but this markup is saved to the DB
// and re-rendered where the editor's CSS never loads — read-only comment bodies
// rendered through [innerHTML], and notes copied into Outlook/Word, where
// external CSS doesn't travel at all.
//
// DELIBERATELY no `text-decoration` here. The Underline extension's second parse
// rule matches `style="text-decoration: underline"` (and Strike's matches
// line-through) with `consuming: false`, so emitting it inline made every
// save/reload re-apply a real <u> mark around the token — visible as a stray
// active "U" in the toolbar and <u> wrappers accumulating in stored HTML. The
// underline is supplied by the .vf-ref-token class inside the editor and by the
// browser's default <a> styling everywhere else, so nothing is lost.
function rootInlineStyle(muted: boolean): string {
    return 'white-space:nowrap;color:' + (muted ? '#9ca3af' : '#2563eb') + ';';
}

const ICON_INLINE_STYLE: string =
    'display:inline-block;width:14px;height:14px;margin-right:4px;vertical-align:-2px;text-decoration:none;';

/**
 * Fills defaults and derives `extension` / `fileKind` from whatever the caller
 * supplied. Shared by the insert command and `referenceTokenHtml()` so all three
 * insertion paths — drag/drop upload, "/" search, and the Files-tab
 * "Add to task description" action — cannot drift apart.
 */
export function normalizeReferenceTokenAttrs(attrs: Partial<ReferenceTokenAttrs>): ReferenceTokenAttrs {
    const type: ReferenceTokenType = attrs.type || 'file';
    const ext: string = attrs.extension || extensionFromFileName(attrs.name || '');

    return {
        type,
        fileKind: type === 'file' ? (attrs.fileKind || fileKindFromExtension(ext)) : null,
        itemId: attrs.itemId || '',
        driveId: attrs.driveId || '',
        name: attrs.name || '',
        url: attrs.url || '',
        status: attrs.status || 'ready',
        siteId: attrs.siteId || '',
        folderId: attrs.folderId || '',
        breadcrumb: attrs.breadcrumb || '',
        tooltipBreadcrumb: attrs.tooltipBreadcrumb || '',
        fileSize: attrs.fileSize || 0,
        extension: ext,
        entityId: attrs.entityId || 0,
        entityType: attrs.entityType || 0,
        taskId: attrs.taskId || 0,
        contactId: attrs.contactId || 0,
        companyId: attrs.companyId || 0,
        refKey: attrs.refKey || ('ref-' + type + '-' + Date.now())
    };
}

/**
 * Token markup as an HTML STRING, byte-identical to what `renderHTML` serializes.
 *
 * For callers that have to splice a token into a stored description string rather
 * than insert at the caret. The Files-tab "Add to task description" action is the
 * case that needs this: it runs from a different tab, where the notes editor may
 * not be mounted at all, and it writes `taskItem.description` directly before
 * pushing the result back through `setValue()`.
 */
export function referenceTokenHtml(partial: Partial<ReferenceTokenAttrs>): string {
    const attrs: ReferenceTokenAttrs = normalizeReferenceTokenAttrs(partial);
    const muted: boolean = attrs.status === 'deleted' || attrs.status === 'error';
    const data: Record<string, string> = dataAttrsFor(attrs);
    let out = '<a';

    Object.keys(data).forEach((k) => {
        out += ' ' + k + '="' + escapeHtml(data[k]) + '"';
    });
    out += ' href="' + escapeHtml(attrs.url || '#') + '"'
        + ' target="_blank" rel="noopener noreferrer" contenteditable="false"'
        + ' class="vf-ref-token' + (muted ? ' is-muted' : '') + '"'
        + ' style="' + rootInlineStyle(muted) + '">'
        + '<span class="vf-ref-token-icon" style="' + ICON_INLINE_STYLE + '">'
        + iconMarkup(attrs.type, attrs.fileKind)
        + '</span>'
        + '<span class="vf-ref-token-name">' + escapeHtml(attrs.name || '') + '</span>'
        + '</a>';
    return out;
}

/**
 * Minimal duck-typed shape shared by the app's attachment rows and picked-file
 * rows (CommonAttachment, TaskAttachment, file-selector results). Field names
 * differ across them, hence the pairs.
 */
export interface FileTokenSource {
    keyId?: string;
    fileType?: string;
    extension?: string;
    msDriveId?: string;
    driveId?: string;
    msSiteId?: string;
    siteId?: string;
    msFolderId?: string;
    folderId?: string;
    fileSize?: number;
}

/**
 * Token markup for one of the app's file/attachment rows — the single mapping
 * used by every "Add to task description" entry point (Files-tab row menu, the
 * file-row context menu, and the add-file flows' isAddToDesc toggle) across the
 * desktop and mobile task modals. Keeping it here means those four components
 * don't each re-derive the extension and id aliases.
 */
export function fileTokenHtml(name: string, url: string, source?: FileTokenSource | null): string {
    const item: FileTokenSource = source || {};
    const rawType: string = (item.fileType || item.extension || '').toLowerCase();
    const isFolder: boolean = rawType === 'folder' || rawType === 'documentlibrary';
    // Prefer the extension off the file NAME — a stored fileType of 'otherfile'
    // has lost it. Fall back to fileType, which is also what flags folders.
    const ext: string = isFolder ? 'folder' : (extensionFromFileName(name || '') || rawType);

    return referenceTokenHtml({
        type: isFolder ? 'folder' : 'file',
        name: name || '',
        url: url || '',
        extension: ext,
        itemId: item.keyId || '',
        driveId: item.msDriveId || item.driveId || '',
        siteId: item.msSiteId || item.siteId || '',
        folderId: item.msFolderId || item.folderId || '',
        fileSize: item.fileSize || 0
    });
}

/**
 * Plain hyperlink markup, for the other thing the task modals' `insertLink()`
 * serves: a user-created WEB link (Create new › Link), which is not a file and
 * must not render as a file token. Also escapes its inputs, which the
 * hand-concatenated string this replaces did not.
 */
export function plainLinkHtml(name: string, url: string): string {
    return '<a href="' + escapeHtml(url || '') + '" target="_blank" rel="noopener noreferrer">'
        + escapeHtml(name || '') + '</a>';
}

// --- Attribute plumbing ------------------------------------------------------

/** data-* attribute bag written by both renderHTML and the node view. */
function dataAttrsFor(attrs: ReferenceTokenAttrs): Record<string, string> {
    return {
        'data-vf-ref-token': '',
        'data-type': attrs.type || 'file',
        'data-file-kind': attrs.fileKind || '',
        'data-item-id': attrs.itemId || '',
        'data-drive-id': attrs.driveId || '',
        'data-name': attrs.name || '',
        'data-url': attrs.url || '',
        'data-status': attrs.status || 'ready',
        'data-site-id': attrs.siteId || '',
        'data-folder-id': attrs.folderId || '',
        'data-breadcrumb': attrs.breadcrumb || '',
        'data-tooltip-breadcrumb': attrs.tooltipBreadcrumb || '',
        'data-size': String(attrs.fileSize || 0),
        'data-extension': attrs.extension || '',
        'data-entity-id': String(attrs.entityId || 0),
        'data-entity-type': String(attrs.entityType || 0),
        'data-task-id': String(attrs.taskId || 0),
        'data-contact-id': String(attrs.contactId || 0),
        'data-company-id': String(attrs.companyId || 0),
        'data-att': attrs.refKey || ''
    };
}

/** Reads the token's own markup back into attrs. */
function parseTokenEl(el: HTMLElement): Partial<ReferenceTokenAttrs> {
    const type: ReferenceTokenType = (el.getAttribute('data-type') as ReferenceTokenType) || 'file';
    const name: string = el.getAttribute('data-name') || '';
    const ext: string = el.getAttribute('data-extension') || extensionFromFileName(name);

    return {
        type,
        // Re-DERIVED from the extension rather than trusted from data-file-kind.
        // fileKind is a pure function of the extension, so recomputing costs
        // nothing and self-heals notes saved while the mapping was wrong (an
        // early build filed .md/.txt/.rtf under 'doc' and drew a Word icon on
        // them). Only fall back to the stored value when there's no extension.
        fileKind: type === 'file'
            ? (ext ? fileKindFromExtension(ext) : ((el.getAttribute('data-file-kind') as ReferenceFileKind) || 'generic'))
            : null,
        itemId: el.getAttribute('data-item-id') || '',
        driveId: el.getAttribute('data-drive-id') || '',
        name: el.getAttribute('data-name') || '',
        url: el.getAttribute('data-url') || el.getAttribute('href') || '',
        status: (el.getAttribute('data-status') as ReferenceTokenStatus) || 'ready',
        siteId: el.getAttribute('data-site-id') || '',
        folderId: el.getAttribute('data-folder-id') || '',
        breadcrumb: el.getAttribute('data-breadcrumb') || '',
        tooltipBreadcrumb: el.getAttribute('data-tooltip-breadcrumb') || '',
        fileSize: Number(el.getAttribute('data-size') || 0),
        extension: el.getAttribute('data-extension') || '',
        entityId: Number(el.getAttribute('data-entity-id') || 0),
        entityType: Number(el.getAttribute('data-entity-type') || 0),
        taskId: Number(el.getAttribute('data-task-id') || 0),
        contactId: Number(el.getAttribute('data-contact-id') || 0),
        companyId: Number(el.getAttribute('data-company-id') || 0),
        refKey: el.getAttribute('data-att') || ''
    };
}

/**
 * LEGACY: `<span|div data-vf-image-card>` — the upload-path card used before the
 * unification. Always an image (that path only accepted images).
 */
function parseLegacyImageCard(el: HTMLElement): Partial<ReferenceTokenAttrs> {
    const fileName: string = el.getAttribute('data-name') || '';
    const ext: string = extensionFromFileName(fileName);

    return {
        type: 'file',
        fileKind: ext ? fileKindFromExtension(ext) : 'image',
        itemId: el.getAttribute('data-file-id') || '',
        driveId: el.getAttribute('data-drive-id') || '',
        name: fileName,
        url: el.getAttribute('data-src') || '',
        status: 'ready',
        siteId: el.getAttribute('data-site-id') || '',
        folderId: el.getAttribute('data-folder-id') || '',
        breadcrumb: el.getAttribute('data-path') || '',
        tooltipBreadcrumb: '',
        fileSize: Number(el.getAttribute('data-size') || 0),
        extension: ext,
        entityId: 0,
        entityType: 0,
        taskId: 0,
        contactId: 0,
        companyId: 0,
        refKey: ''
    };
}

/** LEGACY: `<span data-vf-mention-card>` — the "/" search chip before unification. */
function parseLegacyMentionCard(el: HTMLElement): Partial<ReferenceTokenAttrs> {
    const mentionType: string = el.getAttribute('data-mention-type') || 'file';
    const isFolder: boolean = el.getAttribute('data-is-folder') === 'true';
    const ext: string = el.getAttribute('data-extension') || '';
    let type: ReferenceTokenType = 'file';

    if (mentionType === 'task') {
        type = 'task';
    } else if (mentionType === 'client') {
        type = 'client';
    } else if (isFolder) {
        type = 'folder';
    }

    return {
        type,
        fileKind: type === 'file' ? fileKindFromExtension(ext) : null,
        itemId: el.getAttribute('data-file-id') || '',
        driveId: el.getAttribute('data-drive-id') || '',
        name: el.getAttribute('data-name') || '',
        url: el.getAttribute('data-web-url') || '',
        status: el.getAttribute('data-is-deleted') === 'true' ? 'deleted' : 'ready',
        siteId: el.getAttribute('data-site-id') || '',
        folderId: el.getAttribute('data-folder-id') || '',
        breadcrumb: el.getAttribute('data-breadcrumb') || '',
        tooltipBreadcrumb: el.getAttribute('data-tooltip-breadcrumb') || '',
        fileSize: Number(el.getAttribute('data-size') || 0),
        extension: ext,
        entityId: Number(el.getAttribute('data-entity-id') || 0),
        entityType: Number(el.getAttribute('data-entity-type') || 0),
        taskId: Number(el.getAttribute('data-task-id') || 0),
        contactId: Number(el.getAttribute('data-contact-id') || 0),
        companyId: Number(el.getAttribute('data-company-id') || 0),
        refKey: el.getAttribute('data-att') || ''
    };
}

export const ReferenceToken = Node.create<ReferenceTokenOptions>({
    name: 'referenceToken',
    group: 'inline',
    inline: true,
    atom: true,
    selectable: true,
    draggable: false,
    // Above the Link mark's `a[href]` rule (priority 100) so our own <a> markup is
    // re-parsed as a token instead of being demoted to a plain hyperlink.
    priority: 1000,

    addOptions() {
        return {
            HTMLAttributes: {},
            onImageClick: () => undefined,
            onFileClick: () => undefined,
            onFolderClick: () => undefined,
            onTaskClick: () => undefined,
            onClientClick: () => undefined
        };
    },

    addAttributes() {
        // Every attribute declares an explicit parseHTML reading its data-* source.
        // Tiptap's default per-attribute parser looks up the bare camelCase name
        // (getAttribute('fileKind')), which is null on our markup — without these a
        // token re-parsed from saved HTML would reset to defaults and render blank.
        //
        // These MUST return null when the attribute is absent, never ''. Tiptap's
        // injectExtensionAttributesToParseRule spreads these results OVER whatever
        // the parse rule's getAttrs returned, skipping only null/undefined. A ''
        // fallback here would therefore clobber every value the legacy
        // parseLegacyImageCard / parseLegacyMentionCard mappers compute from the
        // OLD data-* names — silently blanking ids, urls and types on notes saved
        // before the unification.
        const str = (attr: string) => (el: HTMLElement) => el.getAttribute(attr);
        const num = (attr: string) => (el: HTMLElement) => {
            const raw: string | null = el.getAttribute(attr);
            return raw === null ? null : (Number(raw) || 0);
        };
        return {
            type: { default: 'file', parseHTML: (el: HTMLElement) => el.getAttribute('data-type') },
            // Always deferred to the rule's getAttrs, which re-derives it from the
            // extension so a stale/wrong stored value can't win. See parseTokenEl.
            fileKind: { default: null, parseHTML: () => null },
            itemId: { default: '', parseHTML: str('data-item-id') },
            driveId: { default: '', parseHTML: str('data-drive-id') },
            name: { default: '', parseHTML: str('data-name') },
            // No `|| ''` / `|| 'ready'` tail on these two — that would make them
            // non-null on legacy markup and clobber the url the legacy mappers read
            // from data-web-url / data-src, and the 'deleted' status they derive
            // from data-is-deleted.
            url: { default: '', parseHTML: (el: HTMLElement) => el.getAttribute('data-url') || el.getAttribute('href') },
            status: { default: 'ready', parseHTML: (el: HTMLElement) => el.getAttribute('data-status') },
            siteId: { default: '', parseHTML: str('data-site-id') },
            folderId: { default: '', parseHTML: str('data-folder-id') },
            breadcrumb: { default: '', parseHTML: str('data-breadcrumb') },
            tooltipBreadcrumb: { default: '', parseHTML: str('data-tooltip-breadcrumb') },
            fileSize: { default: 0, parseHTML: num('data-size') },
            extension: { default: '', parseHTML: str('data-extension') },
            entityId: { default: 0, parseHTML: num('data-entity-id') },
            entityType: { default: 0, parseHTML: num('data-entity-type') },
            taskId: { default: 0, parseHTML: num('data-task-id') },
            contactId: { default: 0, parseHTML: num('data-contact-id') },
            companyId: { default: 0, parseHTML: num('data-company-id') },
            refKey: { default: '', parseHTML: str('data-att') }
        };
    },

    parseHTML() {
        const rule = (fn: (el: HTMLElement) => Partial<ReferenceTokenAttrs>) =>
            (el: HTMLElement | string) => (typeof el === 'string' ? false : fn(el));
        // PER-RULE priority is required, not just the extension-level `priority`
        // above — Tiptap doesn't propagate one to the other, and ProseMirror's
        // schemaRules() inserts MARK rules ahead of node rules at equal priority.
        // Without this, the Link mark's `a[href]` rule (default 50) would claim our
        // serialized <a> on reload and every saved token would degrade into a plain
        // hyperlink.
        const P = 1000;
        return [
            // Current markup.
            { tag: 'a[data-vf-ref-token]', priority: P, getAttrs: rule(parseTokenEl) },
            { tag: 'span[data-vf-ref-token]', priority: P, getAttrs: rule(parseTokenEl) },
            // Legacy markup from notes saved before the two nodes were unified.
            { tag: 'span[data-vf-image-card]', priority: P, getAttrs: rule(parseLegacyImageCard) },
            { tag: 'div[data-vf-image-card]', priority: P, getAttrs: rule(parseLegacyImageCard) },
            { tag: 'span[data-vf-mention-card]', priority: P, getAttrs: rule(parseLegacyMentionCard) }
        ];
    },

    renderHTML({ HTMLAttributes }) {
        const attrs: ReferenceTokenAttrs = HTMLAttributes as any;
        const muted: boolean = attrs.status === 'deleted' || attrs.status === 'error';
        // An <a href> (not a bare span) so the token copies out of the editor as a
        // real hyperlink — pasting notes into Outlook/Word yields a working link —
        // and so read-only surfaces that render saved HTML get click-through free.
        return [
            'a',
            mergeAttributes(this.options.HTMLAttributes, dataAttrsFor(attrs), {
                href: attrs.url || '#',
                target: '_blank',
                rel: 'noopener noreferrer',
                contenteditable: 'false',
                class: 'vf-ref-token' + (muted ? ' is-muted' : ''),
                style: rootInlineStyle(muted)
            }),
            ['span', { class: 'vf-ref-token-icon', style: ICON_INLINE_STYLE }, iconSpec(attrs.type, attrs.fileKind)],
            ['span', { class: 'vf-ref-token-name' }, attrs.name || '']
        ];
    },

    /** Plain-text copy yields the link, per the token's copy-out contract. */
    renderText({ node }) {
        const attrs: ReferenceTokenAttrs = node.attrs as any;
        return attrs.url || attrs.name || '';
    },

    addNodeView() {
        return ({ node }) => {
            const attrs: ReferenceTokenAttrs = node.attrs as any;
            const muted: boolean = attrs.status === 'deleted' || attrs.status === 'error';
            const data: Record<string, string> = dataAttrsFor(attrs);
            // A <span>, NOT the <a> that renderHTML emits. ProseMirror runs
            // handleClickOn from its MOUSEUP handler, so preventDefault there can't
            // stop an anchor's navigation — the token would both route AND follow
            // its href. Serialization still emits a real <a href> (see renderHTML)
            // so copy-out and read-only surfaces keep the link; only the in-editor
            // view is inert.
            const root: HTMLSpanElement = document.createElement('span');
            const icon: HTMLSpanElement = document.createElement('span');
            const label: HTMLSpanElement = document.createElement('span');
            const tooltipDetail: string = attrs.tooltipBreadcrumb || attrs.breadcrumb || '';
            const sizeText: string = attrs.fileSize ? formatFileSize(attrs.fileSize) : '';
            const tooltipParts: string[] = [escapeHtml(attrs.name || '')];
            const jq: any = (window as any).$ || (window as any).jQuery;
            const hasBsTooltip: boolean = !!(jq && jq.fn && jq.fn.tooltip);

            root.className = 'vf-ref-token' + (muted ? ' is-muted' : '');
            root.setAttribute('role', 'link');
            root.setAttribute('contenteditable', 'false');
            root.setAttribute('style', rootInlineStyle(muted));
            Object.keys(data).forEach((k) => root.setAttribute(k, data[k]));

            icon.className = 'vf-ref-token-icon';
            icon.setAttribute('style', ICON_INLINE_STYLE);
            icon.innerHTML = iconMarkup(attrs.type, attrs.fileKind);
            root.appendChild(icon);

            label.className = 'vf-ref-token-name';
            label.textContent = attrs.name || '';
            root.appendChild(label);

            // Hover detail — the path/size the compact token no longer shows inline.
            if (tooltipDetail) {
                tooltipParts.push(escapeHtml(tooltipDetail));
            }
            if (sizeText) {
                tooltipParts.push(escapeHtml(sizeText));
            }
            if (attrs.status === 'deleted') {
                tooltipParts.push('<i>This item may have been deleted</i>');
            }
            if (attrs.status === 'error') {
                tooltipParts.push('<i>Upload failed</i>');
            }
            if (hasBsTooltip) {
                try {
                    jq(root).tooltip({
                        title: tooltipParts.join(' · '),
                        placement: 'bottom',
                        container: 'body',
                        trigger: 'hover',
                        html: true,
                        // Bootstrap 4 strips <svg>/<i> by default; every interpolated
                        // string above is escaped via escapeHtml, so this is safe.
                        sanitize: false,
                        boundary: 'viewport'
                    });
                } catch { /* ignore */ }
            } else {
                root.title = tooltipParts.join(' · ').replace(/<[^>]+>/g, '');
            }

            return {
                dom: root,
                // No click listeners here on purpose — routing lives in the
                // handleClickOn plugin so it can't go stale. See the file header.
                destroy: () => {
                    if (hasBsTooltip) {
                        try { jq(root).tooltip('dispose'); } catch { /* ignore */ }
                    }
                }
            };
        };
    },

    addProseMirrorPlugins() {
        const opts = this.options;
        const nodeName = this.name;

        return [
            new Plugin({
                key: ReferenceTokenPluginKey,
                props: {
                    /**
                     * Single source of click routing for every token in the document.
                     * ProseMirror hands us the LIVE node from the doc, so the attrs are
                     * always current no matter how many times the surrounding text has
                     * been edited and the view rebuilt.
                     */
                    handleClickOn: (view, _pos, node: PMNode, nodePos: number, event: MouseEvent) => {
                        const attrs: ReferenceTokenAttrs = node.attrs as any;
                        let after = 0;

                        if (node.type.name !== nodeName) {
                            return false;
                        }
                        event.preventDefault();

                        // Clicking a contenteditable=false atom leaves the browser — and
                        // therefore ProseMirror — with the NODE itself selected, so the
                        // very next keystroke would REPLACE the token the user just
                        // clicked. Drop a normal caret just after it instead.
                        after = Math.min(nodePos + node.nodeSize, view.state.doc.content.size);
                        view.dispatch(
                            view.state.tr.setSelection(Selection.near(view.state.doc.resolve(after)))
                        );

                        if (attrs.status === 'deleted' || attrs.status === 'uploading') {
                            return true;
                        }
                        switch (attrs.type) {
                            case 'task':
                                opts.onTaskClick(attrs);
                                return true;
                            case 'client':
                                opts.onClientClick(attrs);
                                return true;
                            case 'folder':
                                opts.onFolderClick(attrs);
                                return true;
                            case 'file':
                                if (attrs.fileKind === 'image') {
                                    opts.onImageClick(attrs);
                                } else {
                                    opts.onFileClick(attrs);
                                }
                                return true;
                            default:
                                return true;
                        }
                    }
                },

                // A token must never carry a mark — see buildMarkStripTr. Runs on
                // every doc change, so it covers setContent() loads and any edit
                // that would otherwise apply formatting across a token. Terminates:
                // the appended transaction re-triggers this once, finds nothing
                // left to strip, and returns null.
                appendTransaction: (transactions, _oldState, newState) => {
                    if (!transactions.some(t => t.docChanged)) {
                        return null;
                    }
                    return buildMarkStripTr(newState);
                }
            })
        ];
    },

    addCommands() {
        return {
            insertReferenceToken:
                (attrs: Partial<ReferenceTokenAttrs>) =>
                ({ commands }) => {
                    return commands.insertContent([
                        { type: this.name, attrs: normalizeReferenceTokenAttrs(attrs) },
                        { type: 'text', text: ' ' }
                    ]);
                }
        };
    }
});
